"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergedVariantName = void 0;
exports.commonNamePrefix = commonNamePrefix;
exports.suggestMergeValues = suggestMergeValues;
exports.markMergeRows = markMergeRows;
exports.mergeAxes = mergeAxes;
exports.valuesOnAxes = valuesOnAxes;
const optionLevels_1 = require("./optionLevels");
const SIZE_RE = /\b\d+(?:\.\d+)?\s?(?:oz|g|gm|gram|grams|kg|lb|lbs|mg|ml|l|ct|count|caps|capsules|pk|pack|pc|pcs)\b/i;
const FORM_WORDS = ['powder', 'capsules', 'capsule', 'caps', 'tablets', 'tablet', 'extract', 'liquid', 'shot', 'shots', 'gummies', 'gummy', 'tea', 'leaf', 'resin', 'tincture'];
const SIZE_LEVELS = new Set(['size', 'weight', 'count', 'volume', 'pack', 'quantity', 'qty']);
const FORM_LEVELS = new Set(['form', 'format', 'type', 'kind']);
const norm = (s) => s.replace(/\s*-\s*/g, ' - ').replace(/\s{2,}/g, ' ').trim();
const words = (s) => norm(s).split(' ').filter((w) => w && w !== '-');
const titleCase = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
/** Longest whole-word prefix shared by every name ("" when fewer than 2 names). */
function commonNamePrefix(names) {
    if (names.length < 2)
        return '';
    const lists = names.map(words);
    const out = [];
    for (let i = 0; i < Math.min(...lists.map((l) => l.length)); i++) {
        const w = lists[0][i];
        if (lists.every((l) => l[i].toLowerCase() === w.toLowerCase()))
            out.push(w);
        else
            break;
    }
    // Never swallow a whole name: one product must keep something to map.
    return out.length && lists.some((l) => l.length === out.length) ? out.slice(0, -1).join(' ') : out.join(' ');
}
/**
 * Suggest a value for every level. Order of trust:
 *  1. a variant option whose name matches the level (case-insensitive)
 *  2. a size pattern for Size/Weight/Count… levels, a known form word for Form/Type… levels
 *  3. leftover text (product name minus the shared prefix, minus used words, plus unused
 *     option values) — only when exactly ONE level is still empty; marks the row uncertain.
 */
function suggestMergeValues(levels, src, prefix) {
    const opts = src.options || {};
    const values = {};
    const usedOptionKeys = new Set();
    let rest = norm(src.productName);
    if (prefix && rest.toLowerCase().startsWith(prefix.toLowerCase()))
        rest = rest.slice(prefix.length);
    // Separate remainder for the variant-name side of the pool, so a match found there is
    // actually removed before the NEXT same-category level looks — otherwise two levels that
    // both fall into SIZE_LEVELS (or both into FORM_LEVELS) would match the identical substring.
    let variantRest = norm(src.variantName);
    for (const level of levels) {
        const key = Object.keys(opts).find((k) => k.toLowerCase() === level.toLowerCase());
        if (key && String(opts[key]).trim()) {
            values[level] = String(opts[key]).trim();
            usedOptionKeys.add(key);
            continue;
        }
        const l = level.toLowerCase();
        const pool = `${rest} ${variantRest}`;
        if (SIZE_LEVELS.has(l)) {
            const optHit = Object.entries(opts).find(([k, v]) => !usedOptionKeys.has(k) && SIZE_RE.test(String(v)));
            if (optHit) {
                values[level] = String(optHit[1]).trim();
                usedOptionKeys.add(optHit[0]);
                continue;
            }
            const m = pool.match(SIZE_RE);
            if (m) {
                values[level] = m[0].replace(/\s+/g, ' ').trim();
                rest = rest.replace(m[0], ' ');
                variantRest = variantRest.replace(m[0], ' ');
                continue;
            }
        }
        if (FORM_LEVELS.has(l)) {
            const w = words(pool).find((x) => FORM_WORDS.includes(x.toLowerCase()));
            if (w) {
                const re = new RegExp(`\\b${w}\\b`, 'i');
                values[level] = titleCase(w);
                rest = rest.replace(re, ' ');
                variantRest = variantRest.replace(re, ' ');
                continue;
            }
        }
        values[level] = '';
    }
    const empty = levels.filter((l) => !values[l]);
    let certain = empty.length === 0;
    if (empty.length === 1) {
        for (const v of Object.values(values))
            if (v)
                rest = rest.replace(new RegExp(`\\b${v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'), ' ');
        const leftover = [words(rest).join(' '), ...Object.entries(opts).filter(([k]) => !usedOptionKeys.has(k)).map(([, v]) => String(v))]
            .map((s) => s.replace(/\s{2,}/g, ' ').trim()).filter(Boolean).join(' ').trim();
        if (leftover) {
            values[empty[0]] = leftover.slice(0, 40);
            certain = false;
        }
    }
    return { values, certain };
}
/** Option values compare case-insensitively ("1 OZ" = "1 Oz"): lower-cased, trimmed. */
const foldValue = (v) => (v || '').trim().toLowerCase();
const comboOf = (levels, v) => levels.map((l) => foldValue(v[l] || '')).join('|');
/** Recompute every row's badge. `taken` = existing target combos (comboKey format "a|b").
 *  Duplicate / taken detection ignores case and surrounding spaces. */
function markMergeRows(rows, levels, taken = []) {
    const counts = new Map();
    for (const r of rows)
        counts.set(comboOf(levels, r.values), (counts.get(comboOf(levels, r.values)) || 0) + 1);
    const takenSet = new Set(taken.map((t) => t.split('|').map(foldValue).join('|')));
    return rows.map((r) => {
        const vals = levels.map((l) => (r.values[l] || '').trim());
        const key = comboOf(levels, r.values);
        let match;
        if (vals.some((v) => !v))
            match = 'empty';
        else if (vals.some((v) => v.length > 40 || v.includes(' / ')))
            match = 'invalid';
        else if ((counts.get(key) || 0) > 1 || takenSet.has(key))
            match = 'duplicate';
        else
            match = r.certain ? 'matched' : 'check';
        return { ...r, match };
    });
}
/** Option axes for the target: existing values first (their order), then new values in row order. */
function mergeAxes(levels, rows, existing = []) {
    return levels.map((name) => {
        const prev = existing.find((a) => a.name.toLowerCase() === name.toLowerCase());
        const values = prev ? [...prev.values].sort((a, b) => a.sortOrder - b.sortOrder) : [];
        const seen = new Set(values.map((v) => v.value.toLowerCase()));
        for (const r of rows) {
            const v = (r.values[name] || '').trim();
            if (v && !seen.has(v.toLowerCase())) {
                seen.add(v.toLowerCase());
                values.push({ value: v, sortOrder: values.length });
            }
        }
        return { ...(prev || {}), name: prev?.name || name, values: values.map((v, i) => ({ ...v, sortOrder: i })) };
    });
}
/**
 * A row's values rewritten to the spelling its axis already uses ("1 OZ" → "1 Oz" when the
 * axis holds "1 Oz"), so a moved variant's option value always exists on its axis.
 * Values the axis doesn't hold are returned trimmed.
 */
function valuesOnAxes(levels, values, axes) {
    const out = {};
    for (const level of levels) {
        const v = (values[level] || '').trim();
        const axis = axes.find((a) => a.name.toLowerCase() === level.toLowerCase());
        const hit = axis?.values.find((x) => foldValue(x.value) === foldValue(v));
        out[level] = hit ? hit.value : v;
    }
    return out;
}
const mergedVariantName = (levels, values) => (0, optionLevels_1.variantNameFromOptions)(levels.map((name) => ({ name, values: [] })), values);
exports.mergedVariantName = mergedVariantName;
//# sourceMappingURL=productMerge.js.map